import { IconAlertTriangle, IconAward, IconBrandApple, IconBrandGoogle, IconBuildingBank, IconCalendarDue, IconCircleCheck, IconCreditCard, IconDeviceMobile, IconHourglass, IconMinus, IconPlus, IconX } from "@tabler/icons-react";
import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError, type InstalmentOption, type NextPayment, type Payment, type PaymentMethod, type PaymentsView, type PayResult, type SimulateOutcome, type TripLoyalty } from "../api";
import { formatDate, formatMoney, formatNumber } from "../format";
import { LOYALTY_ERROR_KEYS, PAYMENT_ERROR_KEYS, PLAN_STATUS_KEYS, type Key } from "../i18n";
import { useSession } from "../session";
import { Card, Empty, ErrorNote, SectionTitle, Spinner, StatusChip } from "./ui";

const METHODS: { id: PaymentMethod; key: Key; icon: ReactNode }[] = [
  { id: "card", key: "methodCard", icon: <IconCreditCard size={18} stroke={1.5} /> },
  { id: "mbway", key: "methodMbway", icon: <IconDeviceMobile size={18} stroke={1.5} /> },
  { id: "transfer", key: "methodTransfer", icon: <IconBuildingBank size={18} stroke={1.5} /> },
  { id: "apple_pay", key: "methodApplePay", icon: <IconBrandApple size={18} stroke={1.5} /> },
  { id: "google_pay", key: "methodGooglePay", icon: <IconBrandGoogle size={18} stroke={1.5} /> },
];

export function paymentErrorMessage(err: unknown, t: (key: Key) => string): string {
  if (err instanceof ApiError) {
    const key = PAYMENT_ERROR_KEYS[err.code];
    if (key) return t(key);
  }
  if (err instanceof TypeError) return t("offline");
  return t("somethingWrong");
}

export function paymentKindLabel(p: { kind: Payment["kind"]; instalmentNo: number | null }, t: (key: Key, vars?: Record<string, string | number>) => string) {
  if (p.kind === "instalment") return t("instalmentN", { n: p.instalmentNo ?? "" });
  if (p.kind === "deposit") return t("deposit");
  if (p.kind === "balance") return t("balance");
  return t("refund");
}

/**
 * The payment plan of a trip (docs/16 decision 11): deposit, balance date, progress, the list of payments and the Pay
 * button for the next one. `openPay` opens the sheet at once (a payment link or a fresh acceptance).
 */
export function PaymentsCard({ tripId, holder, openPay, onChanged }: { tripId: string; holder: boolean; openPay?: boolean; onChanged?: (view: PaymentsView | null) => void }) {
  const { lang, t } = useSession();
  const [view, setView] = useState<PaymentsView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [sheet, setSheet] = useState<NextPayment | null>(null);
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    setError(null);
    api
      .payments(tripId)
      .then((res) => {
        setView(res.payments);
        onChanged?.(res.payments);
      })
      .catch((err: unknown) => {
        setView(null);
        setError(paymentErrorMessage(err, t));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, tick]);

  useEffect(() => {
    if (openPay && holder && view?.next && !autoOpened) {
      setSheet(view.next);
      setAutoOpened(true);
    }
  }, [openPay, holder, view, autoOpened]);

  const apply = (next: PaymentsView | null) => {
    setView(next);
    onChanged?.(next);
  };

  return (
    <>
      <SectionTitle>{t("payments")}</SectionTitle>
      {view === undefined ? (
        <Card>
          <Spinner />
        </Card>
      ) : error ? (
        <ErrorNote onRetry={() => setTick((n) => n + 1)}>{error}</ErrorNote>
      ) : !view ? (
        <Empty title={t("noPlanYet")} />
      ) : (
        <Card className="payments-card">
          <PlanSummary view={view} />
          <ul className="list">
            {view.payments
              .filter((p) => p.status !== "cancelled")
              .map((p) => (
                <li key={p.id} className="list-row payment-row">
                  <span className="list-icon">{paymentIcon(p)}</span>
                  <span className="list-text">
                    <strong>{paymentKindLabel(p, t)}</strong>
                    <span className="muted small">
                      {p.status === "paid" && p.settledAt ? formatDate(p.settledAt.slice(0, 10), lang) : p.dueOn ? t("dueOn", { date: formatDate(p.dueOn, lang) }) : ""}
                      {p.failureReason && p.status === "failed" ? ` · ${p.failureReason}` : ""}
                    </span>
                  </span>
                  <span className="list-aside payment-amount">
                    {p.kind === "refund" ? "−" : ""}
                    {formatMoney(p.amountMinor, p.currency, lang)}
                  </span>
                  <StatusChip status={p.status} lang={lang} />
                </li>
              ))}
          </ul>
          {holder && view.next && (
            <button type="button" className="button button-primary button-block" onClick={() => setSheet(view.next)}>
              {t("payAmount", { amount: formatMoney(view.next.amountMinor, view.plan.currency, lang) })}
            </button>
          )}
          {!holder && <p className="muted small">{t("companionReadOnly")}</p>}
          {holder && view.totals.outstanding > 0 && <RedeemPoints tripId={tripId} currency={view.plan.currency} outstanding={view.totals.outstanding} onApplied={apply} />}
          {holder && view.plan.instalments === null && view.plan.instalmentOptions.length > 0 && ["deposit_due", "deposit_paid", "balance_due"].includes(view.plan.status) && (
            <InstalmentsOffer tripId={tripId} options={view.plan.instalmentOptions} balanceDueOn={view.plan.balanceDueOn} currency={view.plan.currency} onChosen={apply} />
          )}
        </Card>
      )}
      {sheet && view && <PaySheet tripId={tripId} payment={sheet} currency={view.plan.currency} onClose={() => setSheet(null)} onPaid={apply} />}
    </>
  );
}

function paymentIcon(p: Payment) {
  switch (p.status) {
    case "paid":
      return <IconCircleCheck size={20} stroke={1.5} />;
    case "failed":
      return <IconAlertTriangle size={20} stroke={1.5} />;
    case "pending":
      return <IconHourglass size={20} stroke={1.5} />;
    default:
      return <IconCalendarDue size={20} stroke={1.5} />;
  }
}

function PlanSummary({ view }: { view: PaymentsView }) {
  const { lang, t } = useSession();
  const { plan, totals } = view;
  const pct = totals.due > 0 ? Math.min(100, Math.round((totals.paid / totals.due) * 100)) : 100;
  const planKey = PLAN_STATUS_KEYS[plan.status];
  return (
    <div className="plan-summary">
      <div className="plan-head">
        <div>
          <span className="eyebrow">{planKey ? t(planKey) : plan.status}</span>
          <strong className="plan-paid">{t("paidOf", { paid: formatMoney(totals.paid, totals.currency, lang), total: formatMoney(totals.due, totals.currency, lang) })}</strong>
        </div>
        <div className="plan-outstanding">
          <span className="muted small">{t("outstanding")}</span>
          <strong>{formatMoney(totals.outstanding, totals.currency, lang)}</strong>
        </div>
      </div>
      <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <p className="muted small">
        {t("deposit")} {formatMoney(plan.depositMinor, plan.currency, lang)} · {plan.instalments ? t("instalmentsChosen", { n: plan.instalments.length }) : t("balanceDueOn", { date: formatDate(plan.balanceDueOn, lang) })}
      </p>
      {plan.pointsApplied > 0 && (
        <p className="small points-applied">
          <IconAward size={16} /> {t("pointsApplied", { points: formatNumber(plan.pointsApplied, lang), value: formatMoney(plan.pointsValueMinor, plan.currency, lang) })}
        </p>
      )}
    </div>
  );
}

/**
 * "Use your Getaways Club points" (docs/16 §2.9): the redeemable balance on this trip in hundreds of points, applied to the
 * open balance through the CRM ledger. Shown to the holder while something is outstanding; silent when nothing can be used.
 */
function RedeemPoints({ tripId, currency, outstanding, onApplied }: { tripId: string; currency: string; outstanding: number; onApplied: (view: PaymentsView | null) => void }) {
  const { brand, lang, t } = useSession();
  const [data, setData] = useState<TripLoyalty | null | undefined>(undefined);
  const [points, setPoints] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ points: number; valueMinor: number } | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .tripLoyalty(tripId)
      .then((res) => {
        if (!alive) return;
        setData(res);
        setPoints(res.redeemable.points);
      })
      .catch(() => {
        if (alive) setData(null);
      });
    return () => {
      alive = false;
    };
  }, [tripId, outstanding]);

  if (!data || data.redeemable.points <= 0) {
    if (done) {
      return (
        <p className="saved small">
          <IconCircleCheck size={16} /> {t("pointsApplied", { points: formatNumber(done.points, lang), value: formatMoney(done.valueMinor, currency, lang) })}
        </p>
      );
    }
    return null;
  }
  const max = data.redeemable.points;
  const programme = data.loyalty?.programme ?? brand?.loyaltyProgramme ?? "";
  const step = (delta: number) => setPoints((p) => Math.min(max, Math.max(100, p + delta)));

  const submit = async () => {
    if (busy || points < 100) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.redeemPoints(tripId, points);
      setDone(res.redeemed);
      setData((d) => (d ? { ...d, redeemable: { points: 0, valueMinor: 0 }, applied: { points: (d.applied?.points ?? 0) + res.redeemed.points, valueMinor: (d.applied?.valueMinor ?? 0) + res.redeemed.valueMinor } } : d));
      onApplied(res.payments);
    } catch (err) {
      if (err instanceof ApiError && LOYALTY_ERROR_KEYS[err.code]) setError(t(LOYALTY_ERROR_KEYS[err.code]!));
      else setError(paymentErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="redeem">
      <p className="redeem-title">
        <IconAward size={18} /> <strong>{t("usePoints", { programme })}</strong>
      </p>
      <p className="muted small">{t("redeemBody", { points: formatNumber(max, lang), value: formatMoney(data.redeemable.valueMinor, currency, lang) })}</p>
      <div className="stepper">
        <button type="button" className="icon-button" onClick={() => step(-100)} disabled={busy || points <= 100} aria-label={t("fewerPoints")}>
          <IconMinus size={16} />
        </button>
        <div className="stepper-value">
          <strong>{formatNumber(points, lang)}</strong>
          <span className="muted small">
            {t("points")} · −{formatMoney(points, currency, lang)}
          </span>
        </div>
        <button type="button" className="icon-button" onClick={() => step(100)} disabled={busy || points >= max} aria-label={t("morePoints")}>
          <IconPlus size={16} />
        </button>
      </div>
      <input type="range" min={100} max={max} step={100} value={points} onChange={(e) => setPoints(Number(e.target.value))} aria-label={t("pointsToUse")} disabled={busy} />
      {error && <ErrorNote>{error}</ErrorNote>}
      <button type="button" className="button button-small" disabled={busy} onClick={() => void submit()}>
        {busy ? t("applying") : t("applyPoints")}
      </button>
    </div>
  );
}

function InstalmentsOffer({ tripId, options, balanceDueOn, currency, onChosen }: { tripId: string; options: InstalmentOption[]; balanceDueOn: string; currency: string; onChosen: (view: PaymentsView | null) => void }) {
  const { lang, t } = useSession();
  const [openOffer, setOpenOffer] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const choose = async (months: number) => {
    setBusy(months);
    setError(null);
    try {
      onChosen((await api.chooseInstalments(tripId, months)).payments);
    } catch (err) {
      setError(paymentErrorMessage(err, t));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="instalments">
      <button type="button" className="link-button" onClick={() => setOpenOffer((v) => !v)}>
        {t("payMonthly")}
      </button>
      {openOffer && (
        <div className="stack">
          <p className="muted small">
            {t("payMonthlyBody")} ({formatDate(balanceDueOn, lang)})
          </p>
          {error && <ErrorNote>{error}</ErrorNote>}
          {options.map((o) => (
            <button key={o.months} type="button" className="card instalment-option" disabled={busy !== null} onClick={() => void choose(o.months)}>
              <strong>{t("monthsTimes", { months: o.months, monthly: formatMoney(o.monthly, currency, lang) })}</strong>
              <span className="muted small">{t("firstLast", { first: formatDate(o.firstOn, lang), last: formatDate(o.lastOn, lang) })}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PaySheet({ tripId, payment, currency, onClose, onPaid }: { tripId: string; payment: NextPayment; currency: string; onClose: () => void; onPaid: (view: PaymentsView | null) => void }) {
  const { lang, t } = useSession();
  const [method, setMethod] = useState<PaymentMethod>("card");
  const [simulate, setSimulate] = useState<SimulateOutcome>("paid");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayResult | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.pay(tripId, payment.id, { method, simulate, ...(method === "mbway" && phone.trim() ? { phone: phone.trim() } : {}) });
      // A real gateway answers with its hosted page: the customer pays there and comes back on the trip's link.
      if (res.result.redirectUrl) {
        window.location.assign(res.result.redirectUrl);
        return;
      }
      setResult(res.result);
      onPaid(res.payments);
    } catch (err) {
      setError(paymentErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const amount = formatMoney(payment.amountMinor, currency, lang);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={t("payAmount", { amount })} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <div>
            <span className="eyebrow">{paymentKindLabel(payment, t)}</span>
            <h2 className="sheet-amount">{amount}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t("close")}>
            <IconX size={18} />
          </button>
        </div>

        {result ? (
          <div className={`pay-result pay-result-${result.payment.status}`}>
            {result.payment.status === "paid" ? <IconCircleCheck size={40} stroke={1.25} /> : result.payment.status === "failed" ? <IconAlertTriangle size={40} stroke={1.25} /> : <IconHourglass size={40} stroke={1.25} />}
            <h3>{t(result.payment.status === "paid" ? "payPaid" : result.payment.status === "failed" ? "payFailed" : "payPending")}</h3>
            <p className="muted small">{t(result.payment.status === "paid" ? "payPaidBody" : result.payment.status === "failed" ? "payFailedBody" : "payPendingBody")}</p>
            {result.payment.status === "failed" ? (
              <button type="button" className="button button-primary button-block" onClick={() => setResult(null)}>
                {t("retry")}
              </button>
            ) : (
              <button type="button" className="button button-block" onClick={onClose}>
                {t("close")}
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="field-label">{t("paymentMethod")}</p>
            <div className="methods">
              {METHODS.map((m) => (
                <button key={m.id} type="button" className={`method ${method === m.id ? "on" : ""}`} onClick={() => setMethod(m.id)} aria-pressed={method === m.id}>
                  {m.icon}
                  <span>{t(m.key)}</span>
                </button>
              ))}
            </div>
            {method === "mbway" && (
              <label className="field">
                <span className="field-label">{t("signInPhone")}</span>
                <input type="tel" inputMode="tel" autoComplete="tel" placeholder={t("phonePlaceholder")} value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
            )}
            <fieldset className="simulation">
              <legend>{t("simulation")}</legend>
              <p className="muted small">{t("simulationBody")}</p>
              {(["paid", "failed", "pending"] as const).map((o) => (
                <label key={o} className="checkbox">
                  <input type="radio" name="simulate" value={o} checked={simulate === o} onChange={() => setSimulate(o)} />
                  <span>{t(o === "paid" ? "simApprove" : o === "failed" ? "simDecline" : "simPending")}</span>
                </label>
              ))}
            </fieldset>
            {error && <ErrorNote>{error}</ErrorNote>}
            <button type="button" className="button button-primary button-block" disabled={busy} onClick={() => void submit()}>
              {busy ? t("paying") : t("payNow")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
